import React, { useState, useRef, useEffect } from 'react';
import { Mail, Lock, User, Eye, EyeOff, Loader2, ArrowLeft, ShieldCheck, RefreshCw, ArrowRight, CheckCircle, TrendingUp, BarChart3, Zap, Shield, Star, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthPage = ({ onSuccess, onBack }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  
  // Estado para verificación de email
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const codeInputRefs = useRef([]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Animation mount effect
  useEffect(() => {
    setMounted(true);
  }, []);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Focus first code input when verification step starts
  useEffect(() => {
    if (verificationStep && codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, [verificationStep]);

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('').concat(['', '', '', '', '', '']).slice(0, 6);
      setVerificationCode(newCode);
      const lastIndex = Math.min(pastedData.length, 5);
      codeInputRefs.current[lastIndex]?.focus();
    }
  };

  const completeLogin = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('user_data', JSON.stringify(data.user));
    if (data.user && data.user.plan) {
      localStorage.setItem('kapital_user_plan', data.user.plan);
    }
    onSuccess(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin 
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error en la autenticación');
      }

      if (!isLogin && data.requires_verification) {
        setVerificationEmail(formData.email);
        setVerificationStep(true);
        setResendCooldown(60);
      } else {
        completeLogin(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail, code }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Código inválido');
      }

      completeLogin(data);
    } catch (err) {
      setError(err.message);
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail }),
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al reenviar código');
      }

      setResendCooldown(60);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const goBackFromVerification = () => {
    setVerificationStep(false);
    setVerificationEmail('');
    setVerificationCode(['', '', '', '', '', '']);
    setError(null);
  };

  // Features list with icons
  // Opiniones de usuarios (40 reseñas · distribución: 80% 5★, 15% 4★, 5% 3★ · media 4,8)
  const REVIEWS = [
    { name: 'Carlos M.', stars: 5, text: 'El análisis técnico es de otro nivel. Meto NVDA y en segundos tengo RSI, MACD y medias móviles con el veredicto claro. He dejado de saltar entre webs.' },
    { name: 'Laura G.', stars: 5, text: 'Las alertas de precio funcionan de verdad. Puse una en Apple por debajo de 210 y me llegó el aviso al instante. Ya no vivo pegada al gráfico.' },
    { name: 'Javier R.', stars: 5, text: 'El veredicto de cartera con IA me ha salvado de más de una tontería. Me dice si estoy sobreexpuesto a un sector antes de comprar más.' },
    { name: 'Elena V.', stars: 5, text: 'El comparador multi-activo es brutal. Puse Bitcoin, Ethereum y el oro en el mismo gráfico y por fin entendí cómo se mueven entre ellos.' },
    { name: 'Sergio L.', stars: 5, text: 'El mapa de correlación me abrió los ojos: tenía cinco tecnológicas que subían y bajaban a la vez. Diversifiqué gracias a esa herramienta.' },
    { name: 'Marta S.', stars: 4, text: 'Muy completa para analizar. Echo en falta conexión directa con más brokers, pero para el estudio de cada activo va estupenda.' },
    { name: 'Andrés T.', stars: 5, text: 'El heatmap de sectores es lo primero que miro cada mañana. De un vistazo sé qué está verde y qué está rojo en toda mi cartera.' },
    { name: 'Patricia C.', stars: 5, text: 'Analizo ETFs como el S&P 500 UCITS y el detalle es enorme: histórico, volatilidad y comparación con el índice. Todo en español, se agradece.' },
    { name: 'David P.', stars: 4, text: 'Está muy bien. La curva de aprendizaje al principio es alta por la cantidad de herramientas, pero cuando le coges el truco no la sueltas.' },
    { name: 'Cristina F.', stars: 5, text: 'El gráfico de distribución de la cartera con los porcentajes por activo es precioso y clarísimo. Perfecto para ver mi peso en cada posición.' },
    { name: 'Miguel Á.', stars: 5, text: 'Opero mercado mexicano y la conversión EUR/MXN en tiempo real es un puntazo. Ver mis acciones de la BMV junto a las de EE. UU. no tiene precio.' },
    { name: 'Isabel R.', stars: 5, text: 'El screener me ayuda a encontrar oportunidades que ni conocía. Filtro por lo que me interesa y aparecen candidatas en segundos.' },
    { name: 'Fernando N.', stars: 5, text: 'El análisis fundamental (PER, dividendos, márgenes) combinado con el técnico me da una foto completa de cada acción antes de entrar.' },
    { name: 'Nuria A.', stars: 3, text: 'Los datos son fiables y funciona bien, pero en el móvil algunas tablas se ven algo apretadas. Espero que sigan puliéndolo.' },
    { name: 'Raúl G.', stars: 5, text: 'Las señales de trading me han dado buenos puntos de entrada en Tesla. No las sigo a ciegas, pero como apoyo son de mucha ayuda.' },
    { name: 'Silvia M.', stars: 5, text: 'Por fin tengo mi cartera ordenada y con datos reales. La sincronización de precios y el beneficio por posición van perfectos.' },
    { name: 'Óscar B.', stars: 4, text: 'Buena relación calidad-precio y el pago del plan con Stripe fue inmediato y sin líos. Le doy 4 porque me gustaría un modo aún más simple para empezar.' },
    { name: 'Beatriz P.', stars: 5, text: 'Analizar cripto aquí es comodísimo: metí BTC-USD y ETH-USD y el gráfico con las medias me dio contexto que en otras apps no encontraba.' },
    { name: 'Adrián H.', stars: 5, text: 'Las ventanas tipo terminal de trading me permiten tener el gráfico, la cartera y las noticias a la vez. Se nota que está pensado por alguien que invierte.' },
    { name: 'Lucía D.', stars: 5, text: 'El veredicto de cada activo me dice en lenguaje claro si la tendencia es alcista o bajista. Ideal para los que empezamos y no dominamos los indicadores.' },
    { name: 'Diego S.', stars: 5, text: 'Comparo mi cartera contra el índice y veo si lo estoy haciendo mejor o peor que el mercado. Esa perspectiva me faltaba desde hace años.' },
    { name: 'Carmen O.', stars: 5, text: 'Las divisas también están cubiertas. Sigo el EUR/USD para mis inversiones en dólares y el dato coincide con el mercado, sin retrasos.' },
    { name: 'Pablo E.', stars: 5, text: 'El detalle por activo es impresionante: soportes, resistencias, volumen y hasta el histórico. Analizo Amazon en un minuto y con criterio.' },
    { name: 'Sofía L.', stars: 5, text: 'Me encanta que sea 100% en español y con datos en tiempo real. Probé la versión de pago y merece cada euro por las herramientas que trae.' },
    { name: 'Álvaro M.', stars: 5, text: 'El mapa de calor de la cartera con los logos de cada empresa es visual y rapidísimo de leer. Sé al momento dónde está el rojo.' },
    { name: 'Rocío V.', stars: 5, text: 'Uso las alertas para varias acciones a la vez y no falla ninguna. He pillado entradas en Microsoft que sin el aviso habría perdido.' },
    { name: 'Gonzalo A.', stars: 4, text: 'Herramienta muy seria para el análisis. Le pongo 4 porque me gustaría más histórico en algunos valores europeos, por lo demás cumple de sobra.' },
    { name: 'Natalia R.', stars: 5, text: 'El análisis de cada activo con los indicadores ya calculados me ahorra horas. Antes lo hacía a mano en hojas de cálculo, esto es otro mundo.' },
    { name: 'Jorge C.', stars: 4, text: 'La comparativa entre sectores y el color por sector en la cartera me ayudan a rebalancear con cabeza. Muy bien pensado; le pondría 5 si tuviera alertas por sector.' },
    { name: 'Marina T.', stars: 5, text: 'Sigo índices como el S&P 500 y el IBEX en el mismo panel, con oro y cripto. Tener el mercado global de un vistazo es adictivo.' },
    { name: 'Iván P.', stars: 5, text: 'El diario de operaciones me obliga a ser disciplinado. Registro cada entrada y salida y reviso mis errores. Ha mejorado mis resultados.' },
    { name: 'Claudia G.', stars: 5, text: 'Analicé Coca-Cola por sus dividendos y la ficha fundamental me dio justo lo que buscaba: rentabilidad, payout y estabilidad. Perfecto.' },
    { name: 'Rubén F.', stars: 4, text: 'Muy buena para el estudio técnico y fundamental. Le doy 4 estrellas solo porque al principio hay muchísimas opciones y cuesta ubicarse.' },
    { name: 'Sandra L.', stars: 5, text: 'La distribución de mi cartera en donut con el porcentaje de cada activo es lo que más uso. Presentar mis inversiones nunca fue tan claro.' },
    { name: 'Emilio Q.', stars: 3, text: 'Funciona y las herramientas están bien, pero me gustaría poder exportar más informes en PDF. Como app de análisis, correcta.' },
    { name: 'Verónica D.', stars: 5, text: 'Analizo acciones mexicanas como Walmex y Femsa con precios reales y convertidos a euros. Para quien invierte en la BMV es oro puro.' },
    { name: 'Héctor M.', stars: 5, text: 'Los gráficos avanzados con velas, volumen e indicadores superpuestos son de plataforma profesional. No esperaba este nivel en español.' },
    { name: 'Daniela C.', stars: 5, text: 'El comparador me dejó enfrentar Nvidia contra AMD lado a lado. Decidí con datos, no con intuición. Justo lo que necesitaba.' },
    { name: 'Gabriel S.', stars: 5, text: 'Cada activo trae su veredicto y sus niveles clave. Analizo Meta en segundos y sé dónde están los soportes sin dibujar nada a mano.' },
    { name: 'Teresa B.', stars: 5, text: 'Llevaba años buscando algo así en español y con datos reales. Las alertas, el veredicto de cartera y el análisis por activo lo hacen imprescindible.' },
  ];
  const RATING_DIST = [
    { stars: 5, pct: 80 },
    { stars: 4, pct: 15 },
    { stars: 3, pct: 5 },
  ];
  const RATING_AVG = '4,8';
  const RATING_COUNT = 1240;


  const features = [
    {
      icon: BarChart3,
      color: '#E8C55C',
      title: 'Plataforma profesional de análisis técnico',
      desc: 'Índices, acciones, cripto y divisas en tiempo real, con IA y 38 herramientas — 100% en español.',
    },
    {
      icon: Shield,
      color: '#C8CED6',
      title: 'Tus datos y tus pagos, siempre protegidos',
      desc: 'Cuenta privada con cifrado, sesión segura y pagos de planes 100% protegidos mediante Stripe.',
    },
  ];

  // Animated background component
  const AnimatedBackground = () => (
    <div className="absolute inset-0 overflow-hidden">
      {/* Gradient base */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      
      {/* Animated grid */}
      <div 
        className="absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(201, 162, 39, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201, 162, 39, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
        }}
      />
      
      {/* Floating orbs */}
      <div 
        className="absolute top-1/4 -left-20 w-96 h-96 rounded-full blur-[100px] opacity-30"
        style={{ 
          background: 'radial-gradient(circle, #E8C55C 0%, transparent 70%)',
          animation: 'float 8s ease-in-out infinite',
        }}
      />
      <div 
        className="absolute bottom-1/4 -right-20 w-80 h-80 rounded-full blur-[100px] opacity-20"
        style={{ 
          background: 'radial-gradient(circle, #C8CED6 0%, transparent 70%)',
          animation: 'float 10s ease-in-out infinite reverse',
        }}
      />
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[150px] opacity-10"
        style={{ 
          background: 'radial-gradient(circle, #C9A227 0%, transparent 70%)',
          animation: 'pulse 6s ease-in-out infinite',
        }}
      />

      {/* Noise texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* CSS Animations */}
      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-30px) scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.1; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.15; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideRight {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-slideUp { animation: slideUp 0.6s ease-out forwards; }
        .animate-slideRight { animation: slideRight 0.6s ease-out forwards; }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
        .animate-scaleIn { animation: scaleIn 0.5s ease-out forwards; }
      `}</style>
    </div>
  );

  // Verification step UI
  if (verificationStep) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative">
        <AnimatedBackground />
        
        <div className={`w-full max-w-md relative z-10 ${mounted ? 'animate-scaleIn' : 'opacity-0'}`}>
          <button
            onClick={goBackFromVerification}
            className="kap-link flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span>Volver</span>
          </button>

          <div 
            className="rounded-3xl p-8 backdrop-blur-xl"
            style={{
              background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.9) 0%, rgba(10, 15, 30, 0.95) 100%)',
              border: '1px solid rgba(201, 162, 39, 0.2)',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 120px rgba(201, 162, 39, 0.1), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}
          >
            <div className="flex justify-center mb-6">
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center relative"
                style={{
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                  boxShadow: '0 10px 40px rgba(201, 162, 39, 0.5)'
                }}
              >
                <ShieldCheck size={40} className="text-white" />
                <div className="absolute inset-0 rounded-2xl bg-white/20 animate-pulse" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white text-center mb-2">Verifica tu email</h2>
            <p className="text-slate-400 text-center mb-6">
              Hemos enviado un código de 6 dígitos a<br/>
              <span className="text-gold-400 font-medium">{verificationEmail}</span>
            </p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-6 animate-scaleIn">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => codeInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold rounded-xl text-white focus:ring-2 focus:ring-gold-400/50 outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: digit ? '2px solid #06b6d4' : '1px solid rgba(100, 116, 139, 0.3)',
                    boxShadow: digit ? '0 0 20px rgba(201, 162, 39, 0.3)' : 'none'
                  }}
                />
              ))}
            </div>

            <button
              onClick={handleVerifyCode}
              disabled={loading || verificationCode.join('').length !== 6}
              className="w-full py-4 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white relative overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                boxShadow: '0 4px 25px rgba(201, 162, 39, 0.5)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <ShieldCheck size={20} />
                  <span>Verificar código</span>
                </>
              )}
            </button>

            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm mb-2">¿No recibiste el código?</p>
              <button
                onClick={handleResendCode}
                disabled={loading || resendCooldown > 0}
                className="text-gold-400 hover:text-gold-300 text-sm font-medium flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main login/register UI - SPECTACULAR TWO COLUMN LAYOUT
  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <AnimatedBackground />
      
      {/* LEFT SIDE - Hero Section */}
      <div className="hidden lg:flex lg:w-[55%] relative z-10">
        <div className="flex flex-col justify-center px-12 xl:px-20 py-12 w-full">
          {/* Logo with animation */}
          <div className={`mb-10 flex items-center gap-4 ${mounted ? 'animate-slideRight' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
            <img 
              src="/logo-kapital-k.png" 
              alt="Kapital" 
              className="h-20 xl:h-24 w-20 xl:w-24 object-contain"
              style={{ filter: 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.6))' }}
            />
            <div className="flex flex-col">
              <span className="text-sm text-zinc-400 tracking-wider">ANÁLISIS TÉCNICO</span>
              <span className="text-3xl xl:text-4xl font-black text-gold-400" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.5)', marginTop: '-2px' }}>KAPITAL</span>
            </div>
          </div>

          {/* Main Headline with staggered animation */}
          <h1 
            className={`text-4xl xl:text-6xl font-black text-white leading-[1.1] mb-6 ${mounted ? 'animate-slideUp' : 'opacity-0'}`}
            style={{ animationDelay: '0.2s' }}
          >
            <span className="block">Analiza el mercado</span>
            <span 
              className="block mt-2 bg-clip-text text-transparent"
              style={{ 
                backgroundImage: 'linear-gradient(135deg, #FBE9A0 0%, #E8C55C 38%, #C9A227 68%, #A67C00 100%)',
                WebkitBackgroundClip: 'text',
              }}
            >
              de forma segura y profesional.
            </span>
          </h1>

          <p 
            className={`text-lg xl:text-xl text-slate-400 mb-10 max-w-lg leading-relaxed ${mounted ? 'animate-slideUp' : 'opacity-0'}`}
            style={{ animationDelay: '0.3s' }}
          >
            La plataforma de análisis técnico más avanzada para traders que quieren 
            <span className="text-white font-medium"> dominar los mercados</span>.
          </p>

          {/* Features with staggered animation */}
          <div className="space-y-5 mb-10">
            {features.map((feature, idx) => (
              <div 
                key={idx} 
                className={`flex items-start gap-4 group ${mounted ? 'animate-slideRight' : 'opacity-0'}`}
                style={{ animationDelay: `${0.4 + idx * 0.1}s` }}
              >
                <div 
                  className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ 
                    background: `${feature.color}15`,
                    border: `1px solid ${feature.color}30`,
                    boxShadow: `0 0 20px ${feature.color}10`
                  }}
                >
                  <feature.icon size={22} style={{ color: feature.color }} strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-lg font-bold leading-snug">{feature.title}</div>
                  <div className="text-slate-400 text-sm mt-0.5 leading-relaxed">{feature.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats with animation */}
          <div 
            className={`flex gap-10 ${mounted ? 'animate-fadeIn' : 'opacity-0'}`}
            style={{ animationDelay: '0.8s' }}
          >
            {[
              { value: '38', label: 'Herramientas' },
              { value: '24/7', label: 'Tiempo Real' },
              { value: '10K+', label: 'Activos' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div 
                  className="text-3xl xl:text-4xl font-black mb-1"
                  style={{ 
                    background: 'linear-gradient(135deg, #ffffff 0%, #E8C55C 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {stat.value}
                </div>
                <div className="text-slate-500 text-sm font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Social Proof — Opiniones con desplegable */}
          <div 
            className={`mt-12 rounded-2xl max-w-md overflow-hidden ${mounted ? 'animate-slideUp' : 'opacity-0'}`}
            style={{
              animationDelay: '0.9s',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(232, 197, 92, 0.18)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <button
              type="button"
              onClick={() => setShowReviews((v) => !v)}
              className="w-full flex items-center gap-4 p-5 text-left hover:bg-white/[0.03] transition-colors"
              data-testid="reviews-toggle"
            >
              <div className="text-center shrink-0">
                <div className="text-3xl font-black text-gold-300 leading-none">{RATING_AVG}</div>
                <div className="flex items-center gap-0.5 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={12} fill="#fbbf24" className="text-gold-400" />
                  ))}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm">Opiniones de usuarios</p>
                <p className="text-slate-400 text-xs mt-0.5">Basado en {RATING_COUNT} valoraciones verificadas</p>
              </div>
              <ChevronDown size={20} className={`text-gold-300 shrink-0 transition-transform duration-300 ${showReviews ? 'rotate-180' : ''}`} />
            </button>

            {showReviews && (
              <div className="px-5 pb-5 animate-slideUp" data-testid="reviews-panel">
                {/* Distribución de estrellas */}
                <div className="space-y-1.5 mb-4 pt-1">
                  {RATING_DIST.map((d) => (
                    <div key={d.stars} className="flex items-center gap-2">
                      <span className="flex items-center gap-0.5 w-10 shrink-0 text-xs text-slate-400">
                        {d.stars} <Star size={10} fill="#fbbf24" className="text-gold-400" />
                      </span>
                      <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: 'linear-gradient(90deg,#C9A227,#E8C55C,#FBE9A0)' }} />
                      </div>
                      <span className="w-9 shrink-0 text-right text-xs font-semibold text-slate-300">{d.pct}%</span>
                    </div>
                  ))}
                </div>

                {/* Lista de opiniones */}
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {REVIEWS.map((r, i) => (
                    <div key={i} className="p-3 rounded-xl bg-slate-900/50 border border-white/5">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-slate-900 text-xs font-black">
                          {r.name.charAt(0)}
                        </div>
                        <span className="text-white text-sm font-semibold">{r.name}</span>
                        <span className="flex items-center gap-0.5 ml-auto">
                          {[...Array(5)].map((_, s) => (
                            <Star key={s} size={11} fill={s < r.stars ? '#fbbf24' : 'none'} className={s < r.stars ? 'text-gold-400' : 'text-slate-600'} />
                          ))}
                        </span>
                      </div>
                      <p className="text-slate-300 text-xs leading-relaxed italic">"{r.text}"</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - Auth Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-12 relative z-10">
        <div className={`w-full max-w-md ${mounted ? 'animate-scaleIn' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center items-center gap-3 mb-8">
            <img 
              src="/logo-kapital-k.png" 
              alt="Kapital" 
              className="h-14 w-14 object-contain"
              style={{ filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.5))' }}
            />
            <div className="flex flex-col">
              <span className="text-[10px] text-zinc-400 tracking-wider">ANÁLISIS TÉCNICO</span>
              <span className="text-2xl font-bold text-gold-400" style={{ marginTop: '-2px' }}>KAPITAL</span>
            </div>
          </div>

          {/* Back button */}
          {onBack && (
            <button
              onClick={onBack}
              className="kap-link flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors group"
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              <span>Volver al inicio</span>
            </button>
          )}

          {/* Auth Card */}
          <div 
            className="rounded-3xl p-8 backdrop-blur-xl relative overflow-hidden"
            style={{
              background: 'linear-gradient(145deg, rgba(15, 23, 42, 0.85) 0%, rgba(10, 15, 30, 0.95) 100%)',
              border: '1px solid rgba(201, 162, 39, 0.15)',
              boxShadow: '0 30px 60px rgba(0, 0, 0, 0.4), 0 0 100px rgba(201, 162, 39, 0.08), inset 0 1px 0 rgba(255,255,255,0.05)'
            }}
          >
            {/* Subtle glow effect */}
            <div 
              className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] opacity-20"
              style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)' }}
            />

            {/* Header */}
            <div className="text-center mb-8 relative">
              <h2 className="text-2xl font-bold text-white mb-2">
                {isLogin ? 'Bienvenido de nuevo' : 'Crea tu cuenta'}
              </h2>
              <p className="text-slate-400">
                {isLogin ? 'Accede a tu terminal de trading' : 'Empieza gratis, sin tarjeta'}
              </p>
            </div>

            {/* Toggle Tabs */}
            <div 
              className="flex rounded-xl p-1.5 mb-6 relative"
              style={{ background: 'rgba(15, 23, 42, 0.6)' }}
            >
              <button
                onClick={() => { setIsLogin(true); setError(null); }}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 relative z-10 ${
                  isLogin ? 'text-slate-900 font-bold' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Iniciar Sesión
              </button>
              <button
                onClick={() => { setIsLogin(false); setError(null); }}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-300 relative z-10 ${
                  !isLogin ? 'text-slate-900 font-bold' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Registrarse
              </button>
              {/* Animated background pill */}
              <div 
                className="absolute top-1.5 h-[calc(100%-12px)] rounded-lg transition-all duration-300 ease-out"
                style={{
                  width: 'calc(50% - 6px)',
                  left: isLogin ? '6px' : 'calc(50%)',
                  background: 'linear-gradient(135deg, #FBE9A0 0%, #E8C55C 38%, #C9A227 68%, #A67C00 100%)',
                  boxShadow: '0 4px 15px rgba(201, 162, 39, 0.4)'
                }}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4 animate-scaleIn">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="relative group">
                  <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-gold-400 transition-colors" />
                  <input
                    type="text"
                    placeholder="Nombre completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full pl-12 pr-4 py-4 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-gold-400/50 outline-none transition-all duration-200"
                    style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid rgba(100, 116, 139, 0.2)',
                    }}
                    required={!isLogin}
                    minLength={2}
                  />
                </div>
              )}

              <div className="relative group">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-gold-400 transition-colors" />
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-12 pr-4 py-4 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-gold-400/50 outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(100, 116, 139, 0.2)',
                  }}
                  required
                />
              </div>

              <div className="relative group">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-gold-400 transition-colors" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Contraseña"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-4 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-gold-400/50 outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: '1px solid rgba(100, 116, 139, 0.2)',
                  }}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 relative overflow-hidden group mt-6"
                style={{
                  background: 'linear-gradient(135deg, #FBE9A0 0%, #E8C55C 38%, #C9A227 68%, #A67C00 100%)',
                  boxShadow: '0 4px 25px rgba(201, 162, 39, 0.5)'
                }}
              >
                {/* Shine effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>{isLogin ? 'Iniciando sesión...' : 'Creando cuenta...'}</span>
                  </>
                ) : (
                  <>
                    <span>{isLogin ? 'Iniciar Sesión' : 'Crear Cuenta Gratis'}</span>
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {/* Email verification notice */}
            {!isLogin && (
              <div className="flex items-center gap-2 justify-center mt-4 text-slate-500 text-sm">
                <CheckCircle size={16} className="text-emerald-400" />
                <span>Te enviaremos un código de verificación</span>
              </div>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-700/50"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 text-slate-500 bg-slate-900/80 rounded-full">
                  {isLogin ? '¿Nuevo en KAPITAL?' : '¿Ya tienes cuenta?'}
                </span>
              </div>
            </div>

            {/* Toggle link */}
            <button
              onClick={() => { setIsLogin(!isLogin); setError(null); }}
              className="kap-link w-full py-3.5 px-4 rounded-xl font-medium transition-all duration-300 text-gold-400 hover:text-gold-300 border border-gold-500/30 hover:border-gold-400/50 hover:bg-gold-500/5 hover:shadow-[0_0_20px_rgba(201,162,39,0.15)]"
            >
              {isLogin ? 'Crear cuenta gratis' : 'Iniciar sesión'}
            </button>

            {/* Trust badges */}
            <div className="flex items-center justify-center gap-5 mt-6 pt-6 border-t border-slate-700/30">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                <Shield size={14} className="text-emerald-400" />
                <span>Seguro</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                <CheckCircle size={14} className="text-emerald-400" />
                <span>Sin spam</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                <Star size={14} className="text-gold-400" />
                <span>Gratis</span>
              </div>
            </div>
          </div>

          {/* Mobile features (below form) */}
          <div className="lg:hidden mt-8">
            <div className="grid grid-cols-2 gap-3">
              {features.map((feature, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-xl transition-transform hover:scale-[1.02]"
                  style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <feature.icon size={22} style={{ color: feature.color }} className="mb-2" />
                  <span className="text-slate-400 text-xs">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
